import { useEffect, useRef } from 'react';

export interface ShortcutMap {
  /** Одиночные клавиши: 'n', 'x', '?'. */
  [key: string]: (() => void) | undefined;
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Сочетания для широкого экрана. Каждое из них дублирует действие, доступное
 * кнопкой: клавиатура ускоряет работу, но ничего не открывает эксклюзивно.
 * Аккорды вида `g t` собираются из двух нажатий с окном в полторы секунды.
 */
export function useShortcuts(map: ShortcutMap, chords: ShortcutMap = {}) {
  const mapRef = useRef(map);
  const chordsRef = useRef(chords);

  // Запись в ref во время рендера запрещена: обработчики обновляются эффектом,
  // поэтому подписка на keydown остаётся единственной за всё время жизни.
  useEffect(() => {
    mapRef.current = map;
    chordsRef.current = chords;
  });

  useEffect(() => {
    let prefix: string | null = null;
    let prefixTimer: ReturnType<typeof setTimeout> | null = null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Native modal dialogs own Escape and focus; global shortcuts must not cancel them.
      if (document.querySelector('dialog[open]')) return;
      if (isTyping(event.target) && event.key !== 'Escape') return;

      const key = event.key;

      if (prefix) {
        const handler = chordsRef.current[`${prefix} ${key.toLowerCase()}`];
        prefix = null;
        if (prefixTimer) clearTimeout(prefixTimer);
        if (handler) {
          event.preventDefault();
          handler();
          return;
        }
      }

      if (key.toLowerCase() === 'g' && Object.keys(chordsRef.current).length > 0) {
        prefix = 'g';
        prefixTimer = setTimeout(() => (prefix = null), 1500);
        return;
      }

      const handler = mapRef.current[key] ?? mapRef.current[key.toLowerCase()];
      if (handler) {
        event.preventDefault();
        handler();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (prefixTimer) clearTimeout(prefixTimer);
    };
  }, []);
}
